/**
 * Configuration Guide for Adaptive Scroll Behavior
 * ----------------------------------------------
 *
 * RAPID_SCROLL_WINDOW: 1000
 * ------------------------
 * How long we consider scrolls to be "related" and part of the same rapid scroll behavior
 * - Increase (2000-3000ms): If adaptation isn't kicking in during fast scrolls
 * - Decrease (750ms): If system stays in "rapid scroll" mode too long
 * Range: 500-3000ms
 *
 * MAX_RAPID_SCROLL_COUNT: 3
 * ------------------------
 * Maximum number of rapid scrolls we'll count before capping multiplier effects
 * - Increase (5-6): If not fetching enough data during rapid scrolls
 * - Decrease (2): If fetching too much data at once
 * Range: 2-6
 *
 * RAPID_SCROLL_DECAY: 1
 * --------------------
 * How quickly the system returns to normal behavior
 * - Decrease (0.5): If system returns to normal too quickly
 * - Increase (1.5): If system stays in rapid mode too long
 * Range: 0.25-2
 *
 * BASE_SCROLL_TRIGGER: window.innerHeight * 0.2
 * -------------------------------------------
 * How far from the bottom we start loading more content
 * - Increase (0.3): If loading starts too late
 * - Decrease (0.15): If loading starts too early
 * Range: 0.1-0.4 of window height
 *
 * MAX_BATCH_MULTIPLIER: 2
 * ----------------------
 * Maximum multiplication factor for batch size during rapid scrolls
 * - Increase (3-4): If not keeping up with fast scrolling
 * - Decrease (1.5): If loading too much data at once
 * Range: 1.5-4
 *
 * MAX_BUFFER_MULTIPLIER: 2
 * ----------------------
 * Maximum multiplication factor for how early we start loading
 * - Increase (3): If content isn't loading early enough during fast scrolls
 * - Decrease (1.5): If content is loading too early
 * Range: 1.5-3
 *
 * BUFFER_MULTIPLIER_INCREMENT: 0.5
 * ------------------------------
 * How quickly the buffer grows with each rapid scroll
 * - Increase (0.75): If buffer isn't growing fast enough
 * - Decrease (0.25): If buffer is growing too quickly
 * Range: 0.25-1
 *
 * BASE_BATCH_MULTIPLIER_INCREMENT: 0.5
 * -------------------------------------
 * How quickly the batch size grows with each rapid scroll
 * - Increase (0.75): If initial batch size is too small
 * - Decrease (0.25): If initial batch size is too large
 * Range: 0.25-1
 *
 * MAX_BATCH_SIZE: 100
 * -------------------
 * Maximum number of items to fetch in a single batch, highly dependent on the API endpoints
 * for the query in question
 * - Increase (100): If not fetching enough data at once
 * - Decrease (5): If fetching too much data at once
 * Range: 5-100
 *
 * Tuning Process
 * -------------
 * 1. Start with RAPID_SCROLL_WINDOW and MAX_RAPID_SCROLL_COUNT
 *    to get the right detection of rapid scrolling
 * 2. Adjust MAX_BATCH_MULTIPLIER and MAX_BUFFER_MULTIPLIER
 *    to get the right amount of data loading
 * 3. Fine-tune with BUFFER_MULTIPLIER_INCREMENT and RAPID_SCROLL_DECAY
 *    for smooth transitions
 */

import { TableSource } from '~/components/shared/DossTable'

// Shared base interfaces between lookup and table scrolling
interface BaseScrollConfig {
  RAPID_SCROLL_WINDOW: number
  MAX_RAPID_SCROLL_COUNT: number
  RAPID_SCROLL_DECAY: number
  BASE_BATCH_MULTIPLIER_INCREMENT: number
  MAX_BATCH_SIZE: number
}

interface LookupScrollConfig extends BaseScrollConfig {
  BASE_SCROLL_TRIGGER: number
  MAX_BUFFER_MULTIPLIER: number
  BUFFER_MULTIPLIER_INCREMENT: number
}

interface TableScrollConfig extends BaseScrollConfig {
  BASE_FETCH_SIZES: {
    [key in TableSource]: number
  }
  BASE_SCROLL_TRIGGER_PERCENTAGE: number
  MAX_TRIGGER_MULTIPLIER: number
  TRIGGER_MULTIPLIER_INCREMENT: number
}

// Default configuration for lookup scrolling
export const LOOKUP_SCROLL_CONFIG: LookupScrollConfig = {
  RAPID_SCROLL_WINDOW: 2000,
  MAX_RAPID_SCROLL_COUNT: 5,
  RAPID_SCROLL_DECAY: 1.5,
  // Lookup Scroll Trigger is based on px, not percentage
  BASE_SCROLL_TRIGGER: typeof window !== 'undefined' ? window.innerHeight * 0.4 : 250,
  BASE_BATCH_MULTIPLIER_INCREMENT: 1,
  MAX_BATCH_SIZE: 100,
  MAX_BUFFER_MULTIPLIER: 4,
  BUFFER_MULTIPLIER_INCREMENT: 1.0,
}

// Default configuration for table scrolling
export const TABLE_SCROLL_CONFIG: TableScrollConfig = {
  RAPID_SCROLL_WINDOW: 2000,
  MAX_RAPID_SCROLL_COUNT: 5,
  RAPID_SCROLL_DECAY: 1,
  BASE_FETCH_SIZES: {
    CHILD_IN_RECORD_DETAIL: 50,
    TABLE_DETAIL: 50,
    PIVOT_TABLE_DRILLDOWN: 50,
    TABLE_IN_WORKSPACE_DASHBOARD: 5,
    TABLE_IN_PROJECT_DETAIL: 5,
    CHILD_IN_TABLE_DETAIL: 5,
  },
  BASE_BATCH_MULTIPLIER_INCREMENT: 1,
  MAX_BATCH_SIZE: 50,
  // Table Scroll Trigger is based on percentage, not px
  BASE_SCROLL_TRIGGER_PERCENTAGE: 0.3,
  MAX_TRIGGER_MULTIPLIER: 1.5,
  TRIGGER_MULTIPLIER_INCREMENT: 0.25,
}

// Shared interfaces for scroll metrics and state
export interface ScrollMetrics {
  lastFetchTime: number
  rapidScrollCount: number
  nextBatchSize: number
  lastScrollPosition: number
  triggerPoint?: number
}

// Shared state interface for adaptive scroll handlers
export interface AdaptiveScrollState {
  metrics: ScrollMetrics
  reachedTriggerPoint: boolean
  nextBatchSize: number
}

// Adaptive scroll handler for lookup components
export class AdaptiveLookupScroll {
  private metrics: ScrollMetrics
  private baseLimit: number
  private config: LookupScrollConfig

  constructor(baseLimit: number, customConfig: Partial<LookupScrollConfig> = {}) {
    this.baseLimit = baseLimit
    this.config = { ...LOOKUP_SCROLL_CONFIG, ...customConfig }
    this.metrics = {
      lastFetchTime: 0,
      rapidScrollCount: 0,
      nextBatchSize: baseLimit,
      lastScrollPosition: 0,
    }
  }

  // This is the distance (in px) from the bottom of a scrollable element rapidly scrolling,
  // this should trigger earlier (higher up the page) to prevent waiting for data to fetch
  private getTriggerPoint(): number {
    const multiplier = Math.min(
      this.metrics.rapidScrollCount * this.config.BUFFER_MULTIPLIER_INCREMENT,
      this.config.MAX_BUFFER_MULTIPLIER,
    )
    return this.config.BASE_SCROLL_TRIGGER * (1 + multiplier)
  }

  // This is the number of items to fetch in the next batch, determined based on the user's
  // scrolling behavior, faster scrolling will ramp up the batch size using a multiplier (capped)
  private getNextBatchSize(): number {
    if (this.metrics.rapidScrollCount === 0) {
      return this.baseLimit
    }

    const multiplier_count = Math.min(
      this.metrics.rapidScrollCount,
      LOOKUP_SCROLL_CONFIG.MAX_RAPID_SCROLL_COUNT,
    )

    const multiplier = multiplier_count * this.config.BASE_BATCH_MULTIPLIER_INCREMENT

    const batch_size = Math.min(this.baseLimit * (1 + multiplier), this.config.MAX_BATCH_SIZE)

    return batch_size
  }

  // This method should be called whenever the user scrolls the element (or debounced), often in
  // a scroll event listener (in a useEffect or similar hook).
  public updateScrollState(scrollElement: HTMLElement): AdaptiveScrollState {
    const now = Date.now()
    const timeSinceLastFetch = now - this.metrics.lastFetchTime

    // Update rapid scroll metrics
    if (timeSinceLastFetch < this.config.RAPID_SCROLL_WINDOW) {
      this.metrics.rapidScrollCount = Math.min(
        this.metrics.rapidScrollCount + 1,
        this.config.MAX_RAPID_SCROLL_COUNT,
      )
    } else {
      this.metrics.rapidScrollCount = Math.max(
        this.metrics.rapidScrollCount - this.config.RAPID_SCROLL_DECAY,
        0,
      )
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    const triggerPoint = this.getTriggerPoint()
    const reachedTriggerPoint = scrollTop + clientHeight >= scrollHeight - triggerPoint

    if (reachedTriggerPoint) {
      this.metrics.lastFetchTime = now
      this.metrics.lastScrollPosition = scrollTop
    }

    return {
      metrics: this.metrics,
      reachedTriggerPoint,
      nextBatchSize: this.getNextBatchSize(),
    }
  }

  public handleFetchCompleted(receivedCount: number) {
    if (receivedCount < this.metrics.nextBatchSize) {
      this.metrics.nextBatchSize = this.baseLimit
      this.metrics.rapidScrollCount = 0
    }
    this.metrics.lastFetchTime = Date.now()
  }

  public getMetrics(): ScrollMetrics {
    return { ...this.metrics }
  }
}

// Adaptive scroll handler for table components
export class AdaptiveTableScroll {
  private metrics: ScrollMetrics
  private config: TableScrollConfig
  private tableSource: TableSource

  constructor(tableSource: TableSource, customConfig: Partial<TableScrollConfig> = {}) {
    this.tableSource = tableSource
    this.config = { ...TABLE_SCROLL_CONFIG, ...customConfig }
    this.metrics = {
      lastFetchTime: 0,
      rapidScrollCount: 0,
      nextBatchSize: this.getBaseFetchSize(),
      lastScrollPosition: 0,
      triggerPoint: 0,
    }
  }

  // Fixed batch size for the table source (i.e 50 for table_detail, 5 for table_in_project_detail)
  private getBaseFetchSize(): number {
    return this.config.BASE_FETCH_SIZES[this.tableSource]
  }

  // This is the distance (in px) from the bottom of a scrollable element rapidly scrolling,
  // this should trigger earlier (higher up the page) to prevent waiting for data to fetch
  private getTriggerPoint(scrollHeight: number): number {
    const multiplier = Math.min(
      this.metrics.rapidScrollCount * this.config.TRIGGER_MULTIPLIER_INCREMENT,
      this.config.MAX_TRIGGER_MULTIPLIER,
    )

    // Apply the multiplier to the base percentage
    const adjustedPercentage = this.config.BASE_SCROLL_TRIGGER_PERCENTAGE * (1 + multiplier)

    // Calculate trigger point based on total scroll height, but cap it
    const rawTriggerPoint = scrollHeight * adjustedPercentage

    const maxTriggerPoint = scrollHeight * 0.5
    this.metrics.triggerPoint = Math.min(rawTriggerPoint, maxTriggerPoint)

    return this.metrics.triggerPoint
  }

  // This method should be called whenever the user scrolls the element (or debounced), often in
  // a scroll event listener (in a useEffect or similar hook).
  public updateScrollState(scrollElement: HTMLElement): AdaptiveScrollState {
    const now = Date.now()
    const timeSinceLastFetch = now - this.metrics.lastFetchTime

    // Update rapid scroll metrics
    if (timeSinceLastFetch < this.config.RAPID_SCROLL_WINDOW) {
      this.metrics.rapidScrollCount = Math.min(
        this.metrics.rapidScrollCount + 1,
        this.config.MAX_RAPID_SCROLL_COUNT,
      )
    } else {
      this.metrics.rapidScrollCount = Math.max(
        this.metrics.rapidScrollCount - this.config.RAPID_SCROLL_DECAY,
        0,
      )
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    // Use scrollHeight for trigger point calculation
    const triggerPoint = this.getTriggerPoint(scrollHeight)
    const reachedTriggerPoint = scrollTop + clientHeight >= scrollHeight - triggerPoint

    if (reachedTriggerPoint) {
      this.metrics.lastFetchTime = now
      this.metrics.lastScrollPosition = scrollTop
    }

    return {
      metrics: this.metrics,
      reachedTriggerPoint,
      nextBatchSize: this.getBaseFetchSize(),
    }
  }

  public handleFetchCompleted(receivedCount: number) {
    if (receivedCount < this.metrics.nextBatchSize) {
      this.metrics.nextBatchSize = this.getBaseFetchSize()
      this.metrics.rapidScrollCount = 0
    }
    this.metrics.lastFetchTime = Date.now()
  }

  public getMetrics(): ScrollMetrics {
    return { ...this.metrics }
  }
}
